import React, { useContext, useMemo, useRef } from "react";
import styled from "styled-components/native";
import { View } from "react-native";
import Animated from "react-native-reanimated";
import { Container } from "@components/atoms";
import { ProductCategory } from "@utils/constants";
import FilterSection from "@components/atoms/Filters/FilterSection";
import UnifiedFilterSection from "@components/atoms/Filters/UnifiedFilterSection";
import { SpaceType } from "@components/atoms/Spacer/types";
import useHideNavsOnScroll from "@navigations/utils/useHideNavsOnScroll";
import { useIsFocused } from "@react-navigation/native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import { useSelector } from "react-redux";
import {
  combineFiltersByWebWidth,
  mergeFiltersWithSameName,
} from "@scenes/explore/utils";

interface SectionContentProps {
  scrollViewStyles: any;
  handleLayout: (e: any) => void;
  rawFilters: any[];
  isCollection?: boolean;
  theme: any;
  fromResultScreen: boolean;
  sectionName: string;
  productType: ProductCategory;
  filterContainerWidth?: number;
  isOnSplitScreen?: boolean;
  showScrollbars?: boolean;
  enterSecondFlow?: boolean;
  headerComponent?: React.ReactNode;
  sizeToContent?: boolean;
}

const SectionContent: React.FC<SectionContentProps> = ({
  scrollViewStyles,
  handleLayout,
  rawFilters,
  isCollection = false,
  theme,
  fromResultScreen,
  sectionName,
  productType,
  filterContainerWidth,
  isOnSplitScreen,
  showScrollbars,
  enterSecondFlow = false,
  headerComponent,
  sizeToContent = false,
}) => {
  const isFocused = useIsFocused();
  const { scrollHandler } = useHideNavsOnScroll();
  const onScrollHandler = scrollHandler();
  const maxContentLength = 1432;
  const scrollRef = useRef<any>(null);
  const onScroll =
    theme.isLargeScreen && isFocused && !isOnSplitScreen
      ? onScrollHandler
      : undefined;
  const {
    scrollableTabBar,
    renderScrollableTabBar,
    renderScrollableFiltersTabSection,
  } = useContext(FilterScreenContext);

  const selectedCollectionNames = useSelector(
    (state: any) => state.searchFilters.collection
  );
  const prevFilterByIdRef = useRef<Map<string, any>>(new Map());

  const combinedNormalFilters = useMemo(() => {
    const processedFilters = isCollection
      ? mergeFiltersWithSameName(rawFilters, selectedCollectionNames)
      : rawFilters;
    const nextGroups = combineFiltersByWebWidth(processedFilters);
    const prevById = prevFilterByIdRef.current;
    const nextById = new Map<string, any>();
    const stabilized = nextGroups.map((group: any) => ({
      ...group,
      filters: (group.filters || []).map((filter: any) => {
        const id = String(filter._mergeId || filter.name || filter.type);
        const prevFilter = prevById.get(id);
        const reuse =
          prevFilter &&
          (prevFilter.options?.length ?? 0) === (filter.options?.length ?? 0);
        const stable = reuse ? prevFilter : filter;
        nextById.set(id, stable);
        return stable;
      }),
    }));

    prevFilterByIdRef.current = nextById;
    return stabilized;
  }, [isCollection, rawFilters, selectedCollectionNames]);

  const ScrollViewComponent = theme.isLargeScreen
    ? Animated.ScrollView
    : KeyboardAwareScrollView;

  return (
    <ScrollViewComponent
      ref={scrollRef}
      style={[
        sizeToContent
          ? ({
              flexGrow: 0,
              flexShrink: 0,
              // scrollEnabled={false} sets touch-action:none, so a swipe on
              // the filters never reaches the parent page. Leave this view
              // out of the scroll chain instead, same as the results grid.
              overflow: "visible",
              overflowY: "visible",
              touchAction: "pan-y",
              WebkitOverflowScrolling: "auto",
            } as const)
          : { flex: 1 },
        (theme.isLargeScreen || theme.isTablet) &&
          fromResultScreen && { backgroundColor: theme["core-01"] },
      ]}
      contentContainerStyle={scrollViewStyles}
      keyboardDismissMode="on-drag"
      onScroll={sizeToContent ? undefined : onScroll}
      scrollEventThrottle={64}
      showsVerticalScrollIndicator={
        theme.isLargeScreen && showScrollbars && !sizeToContent ? true : false
      }
      {...(theme.isTablet && !sizeToContent && { bottomOffset: 75 })}
    >
      {scrollableTabBar && (
        <View style={{ width: "100%" }}>
          {renderScrollableFiltersTabSection?.()}
        </View>
      )}
      <View
        {...(theme.isLargeScreen &&
          !fromResultScreen && {
            style: {
              width: "80%",
            },
          })}
        {...(!theme.isLargeScreen && { style: { width: "100%" } })}
      >
        <Container
          maxWidth={
            theme.isLargeScreen
              ? maxContentLength
              : theme.isTablet
              ? maxContentLength
              : undefined
          }
        >
          {scrollableTabBar && renderScrollableTabBar?.()}
          {headerComponent}
          <View onLayout={handleLayout}>
            {combinedNormalFilters.map((group, index) => {
              if (Array.isArray(group.filters) && group.filters.length > 1) {
                if (
                  (theme.isLargeScreen || theme.isTablet) &&
                  !fromResultScreen
                ) {
                  return (
                    <UnifiedFiltersSection
                      key={`unified-${index}`}
                      filterData={group.filters.map((filter: any) => ({
                        ...filter,
                      }))}
                      sectionName={sectionName}
                      selectedProduct={productType}
                      fromResultScreen={fromResultScreen}
                      width={filterContainerWidth}
                      filterContainerWidth={filterContainerWidth}
                      scrollRef={scrollRef}
                      enterSecondFlow={enterSecondFlow}
                    />
                  );
                } else {
                  return group.filters.map((_filter: any, subIndex: number) => (
                    <FiltersSection
                      key={
                        _filter._mergeId ||
                        _filter.name ||
                        `normal-${index}-${subIndex}`
                      }
                      filterData={_filter}
                      sectionName={sectionName}
                      selectedProduct={productType}
                      fromResultScreen={fromResultScreen}
                      width={filterContainerWidth}
                      filterContainerWidth={filterContainerWidth}
                      scrollRef={scrollRef}
                      enterSecondFlow={enterSecondFlow}
                    />
                  ));
                }
              } else {
                const filter = group.filters[0];
                return (
                  <FiltersSection
                    key={filter?._mergeId || filter?.name || `normal-${index}`}
                    filterData={filter}
                    sectionName={sectionName}
                    selectedProduct={productType}
                    fromResultScreen={fromResultScreen}
                    filterContainerWidth={filterContainerWidth}
                    scrollRef={scrollRef}
                    enterSecondFlow={enterSecondFlow}
                  />
                );
              }
            })}
          </View>
        </Container>
      </View>
    </ScrollViewComponent>
  );
};

const FiltersSection = styled(FilterSection)<{ width?: number }>`
  margin-top: ${SpaceType.Double}px;
  width: ${({ width }: { width?: number }) =>
    width ? `${width - SpaceType.large}px` : "100%"};
`;

const UnifiedFiltersSection = styled(UnifiedFilterSection)`
  margin-top: ${SpaceType.Double}px;
  width: 100%;
`;

export default React.memo(SectionContent);
